import React, { useState, useCallback, useEffect, useRef } from 'react';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { MapPin, Loader, CheckCircle, Navigation } from 'lucide-react';

const libraries: ("places")[] = ["places"];

const mapContainerStyle = {
  width: '100%',
  height: '400px',
};

const defaultCenter = {
  lat: 28.6139, // Delhi
  lng: 77.2090,
};

interface MapPickerProps {
  initialLocation?: { lat: number; lng: number };
  onLocationSelect: (location: { lat: number; lng: number; address: string }) => void;
  onConfirm?: () => void;
  address?: string;
}

const MapPicker: React.FC<MapPickerProps> = ({
  initialLocation,
  onLocationSelect,
  onConfirm,
  address
}) => {
  // Get Maps API key from environment variable
  // Security: This key must have HTTP referrer restrictions in Google Cloud Console
  const mapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: mapsApiKey,
    libraries,
  });

  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(
    initialLocation || defaultCenter
  );
  const [locationName, setLocationName] = useState<string>(address || 'Selected Location');
  const [isGeocoding, setIsGeocoding] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const isMapMovingRef = useRef(false);
  const geocodeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize location name when component mounts or initialLocation changes
  useEffect(() => {
    if (initialLocation && initialLocation.lat && initialLocation.lng) {
      // Set map center
      setMapCenter({ lat: initialLocation.lat, lng: initialLocation.lng });
      
      // Only reverse geocode if we don't already have a valid address
      if (!address || address === 'Selected Location' || address === 'Getting address...') {
        reverseGeocodeLocation(initialLocation.lat, initialLocation.lng);
      } else {
        setLocationName(address);
        setIsGeocoding(false);
      }
    } else if (address) {
      setLocationName(address);
      setIsGeocoding(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocation?.lat, initialLocation?.lng]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (geocodeTimeoutRef.current) {
        clearTimeout(geocodeTimeoutRef.current);
      }
    };
  }, []);

  const reverseGeocodeLocation = useCallback(async (lat: number, lng: number) => {
    setIsGeocoding(true);
    try {
      const geocoder = new google.maps.Geocoder();
      const result = await geocoder.geocode({ location: { lat, lng } });
      
      if (result.results[0]) {
        const newAddress = result.results[0].formatted_address;
        setLocationName(newAddress);
        onLocationSelect({ lat, lng, address: newAddress });
      } else {
        const fallbackAddress = address || 'Selected Location';
        setLocationName(fallbackAddress);
        onLocationSelect({ lat, lng, address: fallbackAddress });
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      const fallbackAddress = address || 'Selected Location';
      setLocationName(fallbackAddress);
      onLocationSelect({ lat, lng, address: fallbackAddress });
    } finally {
      setIsGeocoding(false);
    }
  }, [onLocationSelect, address]);

  // Handle map drag/pan - update location when map moves
  const handleMapDragStart = useCallback(() => {
    isMapMovingRef.current = true;
  }, []);

  // Handle map idle (when map finishes moving) - this is the main handler
  const handleMapIdle = useCallback(async () => {
    if (mapRef.current && isMapMovingRef.current) {
      // Clear any pending geocode requests
      if (geocodeTimeoutRef.current) {
        clearTimeout(geocodeTimeoutRef.current);
      }
      
      // Debounce geocoding to prevent blinking
      geocodeTimeoutRef.current = setTimeout(async () => {
        const center = mapRef.current?.getCenter();
        if (center) {
          const lat = center.lat();
          const lng = center.lng();
          // Update map center (pin is always at center)
          setMapCenter({ lat, lng });
          // Reverse geocode the new center location
          await reverseGeocodeLocation(lat, lng);
        }
        isMapMovingRef.current = false;
      }, 300); // 300ms debounce to prevent rapid updates
    }
  }, [reverseGeocodeLocation]);

  const handleMyLocation = useCallback(() => {
    if (navigator.geolocation) {
      setIsGeocoding(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          setMapCenter({ lat, lng });
          
          // Center map on user location
          if (mapRef.current) {
            mapRef.current.setCenter({ lat, lng });
            mapRef.current.setZoom(15);
          }
          
          reverseGeocodeLocation(lat, lng);
        },
        (error) => {
          console.error('Error getting location:', error);
          setIsGeocoding(false);
          alert('Could not get your current location. Please enable location permissions.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      alert('Geolocation is not supported by your browser');
    }
  }, [reverseGeocodeLocation]);

  if (!mapsApiKey) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-600">
          Google Maps API key is not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file.
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-600">Error loading Google Maps. Please check your API key.</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-md">
        <Loader className="h-8 w-8 animate-spin text-black600 mr-2" />
        <span className="text-gray-600">Loading Google Maps...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-300 rounded-md p-3">
        <p className="text-sm text-gray-800 flex items-center">
          <MapPin className="h-4 w-4 mr-2" />
          Move the map to adjust the location. The pin is fixed in the center.
        </p>
      </div>

      <div className="relative">
        <GoogleMap
          onLoad={(map) => {
            mapRef.current = map;
          }}
          mapContainerStyle={mapContainerStyle}
          zoom={15}
          center={mapCenter}
          onDragStart={handleMapDragStart}
          onIdle={handleMapIdle}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            zoomControl: false,
            disableDefaultUI: true,
            gestureHandling: 'greedy',
          }}
        >
        </GoogleMap>

        {/* Fixed center pin indicator overlay */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none z-20" style={{ marginTop: '-25px' }}>
          <MapPin className="h-12 w-12 text-black600 drop-shadow-lg" fill="currentColor" />
        </div>

        {/* Location info overlay - similar to Android */}
        <div className="absolute top-4 left-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-3 flex items-center z-30">
          <MapPin className="h-5 w-5 text-black600 mr-2 flex-shrink-0" />
          {isGeocoding ? (
            <div className="flex items-center flex-1">
              <Loader className="h-4 w-4 animate-spin text-black600 mr-2" />
              <span className="text-sm text-gray-600">Getting address...</span>
            </div>
          ) : (
            <span className="text-sm text-gray-700 flex-1 line-clamp-1">
              {locationName || address || 'Move map to adjust location'}
            </span>
          )}
        </div>

        {/* My Location button - similar to Android */}
        <button
          onClick={handleMyLocation}
          className="absolute bottom-4 right-4 bg-white rounded-full shadow-lg p-3 hover:bg-gray-50 transition-colors z-30 border border-gray-200"
          title="My Location"
          style={{ zIndex: 1000 }}
        >
          <Navigation className="h-5 w-5 text-black600" />
        </button>
      </div>

      {onConfirm && (
        <button
          onClick={onConfirm}
          className="w-full flex items-center justify-center px-6 py-3 bg-black text-white rounded-md hover:bg-black700 transition-colors font-semibold"
        >
          <CheckCircle className="h-5 w-5 mr-2" />
          Confirm Location
        </button>
      )}
    </div>
  );
};

export default MapPicker;

